// Search-state for the order-sources index route. AppRouter owns the
// validateSearch schema and stripSearchParams middleware; this hook is the
// typed surface over TanStack Router used by the page components.

import { useCallback, useMemo } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';

import { DEFAULT_PAGE_SIZE } from '@components/DataTable/constants';
import {
  ORDER_SOURCE_STATUS_VALUES,
  type OrderSourceStatus,
} from '@components/OrderSource/types';

export interface OrderSourceSearchParams {
  status: OrderSourceStatus;
  q: string;
  page: number;
  perPage: number;
  sort: string;
  direction: 'asc' | 'desc' | '';
}

export const ORDER_SOURCES_SEARCH_DEFAULTS: OrderSourceSearchParams = {
  status: 'active',
  q: '',
  page: 1,
  perPage: DEFAULT_PAGE_SIZE,
  sort: '',
  direction: '',
};

const ORDER_SOURCES_ROUTE = '/order_sources' as const;
const VALID_DIRECTIONS: ReadonlySet<'asc' | 'desc'> = new Set(['asc', 'desc']);

export function parseOrderSourcesSearch(
  raw: Record<string, unknown>,
): OrderSourceSearchParams {
  const status: OrderSourceStatus = (
    ORDER_SOURCE_STATUS_VALUES as readonly string[]
  ).includes(String(raw.status))
    ? (raw.status as OrderSourceStatus)
    : ORDER_SOURCES_SEARCH_DEFAULTS.status;
  const page = Number(raw.page);
  const perPage = Number(raw.perPage);
  const direction = VALID_DIRECTIONS.has(raw.direction as 'asc' | 'desc')
    ? (raw.direction as 'asc' | 'desc')
    : ORDER_SOURCES_SEARCH_DEFAULTS.direction;

  return {
    status,
    q: typeof raw.q === 'string' ? raw.q : ORDER_SOURCES_SEARCH_DEFAULTS.q,
    page: Number.isFinite(page) && page > 0 ? page : ORDER_SOURCES_SEARCH_DEFAULTS.page,
    perPage:
      Number.isFinite(perPage) && perPage > 0
        ? perPage
        : ORDER_SOURCES_SEARCH_DEFAULTS.perPage,
    sort: typeof raw.sort === 'string' ? raw.sort : ORDER_SOURCES_SEARCH_DEFAULTS.sort,
    direction,
  };
}

export function useOrderSourceRouterSearch() {
  const search = useSearch({ from: ORDER_SOURCES_ROUTE });
  const tanNavigate = useNavigate({ from: ORDER_SOURCES_ROUTE });

  const navigate = useCallback(
    (updates: Partial<OrderSourceSearchParams>) => {
      tanNavigate({
        search: (prev) => ({ ...prev, ...updates }),
        replace: true,
      });
    },
    [tanNavigate],
  );

  return useMemo(
    () => ({
      search,
      navigate,
      setStatus: (status: OrderSourceStatus) => navigate({ status, page: 1 }),
      setQuery: (q: string) => navigate({ q, page: 1 }),
      setPage: (page: number) => navigate({ page }),
      setPerPage: (perPage: number) => navigate({ perPage, page: 1 }),
      setSort: (sort: string, direction: 'asc' | 'desc' | '') =>
        navigate({ sort, direction, page: 1 }),
    }),
    [search, navigate],
  );
}
